/**
 * Dashboard primitives — shared mini-widgets (KPI cards, demo wrappers, charts).
 * Pure SVG / Tailwind / CSS — NO new npm deps.
 *
 * Real-блоки: bg-card, чёткие цифры, цветные акценты по порогам.
 * Demo-блоки: приглушённый фон + бейдж «демо» + подпись «появится после …».
 */

import * as React from 'react'
import type { LucideIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Section heading
// ---------------------------------------------------------------------------

export function SectionTitle({
  icon: Icon,
  title,
  hint,
}: {
  icon?: LucideIcon
  title: string
  hint?: string
}) {
  return (
    <div className="flex items-baseline gap-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-4 w-4" />}
        {title}
      </h2>
      {hint && <span className="text-xs text-muted-foreground/70">{hint}</span>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Demo zone banner + per-block badge
// ---------------------------------------------------------------------------

export function DemoSectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="h-px flex-1 bg-border" />
      <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
          скоро · демо
        </Badge>
        {label}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  )
}

/** Small "демо" badge for a single demo card corner. */
export function DemoBadge({ className }: { className?: string }) {
  return (
    <Badge
      variant="outline"
      className={cn('text-[10px] px-1.5 py-0 border-dashed text-muted-foreground', className)}
    >
      демо
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// KPI card — single number with optional accent + sub-line
// ---------------------------------------------------------------------------

export type Accent = 'neutral' | 'green' | 'amber' | 'red' | 'primary'

const accentText: Record<Accent, string> = {
  neutral: 'text-foreground',
  green: 'text-green-600',
  amber: 'text-amber-600',
  red: 'text-red-600',
  primary: 'text-primary',
}

const accentIcon: Record<Accent, string> = {
  neutral: 'text-muted-foreground',
  green: 'text-green-500',
  amber: 'text-amber-500',
  red: 'text-red-500',
  primary: 'text-primary',
}

const accentBorder: Record<Accent, string> = {
  neutral: '',
  green: 'border-green-200',
  amber: 'border-amber-200',
  red: 'border-red-200',
  primary: 'border-primary/30',
}

export function KpiCard({
  label,
  value,
  icon: Icon,
  accent = 'neutral',
  sub,
  demo = false,
  demoLabel,
  onClick,
  className,
}: {
  label: string
  value: React.ReactNode
  icon?: LucideIcon
  accent?: Accent
  sub?: React.ReactNode
  demo?: boolean
  demoLabel?: string
  onClick?: () => void
  className?: string
}) {
  const clickable = !!onClick
  return (
    <Card
      onClick={onClick}
      className={cn(
        'transition-colors',
        demo ? 'bg-muted/40 border-dashed' : accentBorder[accent],
        clickable && 'cursor-pointer hover:border-primary/50 hover:shadow',
        className,
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-medium text-muted-foreground leading-tight">{label}</p>
          {demo ? (
            <DemoBadge />
          ) : (
            Icon && <Icon className={cn('h-4 w-4 shrink-0', accentIcon[accent])} />
          )}
        </div>
        <p
          className={cn(
            'mt-2 text-2xl font-bold tabular-nums leading-none',
            demo ? 'text-muted-foreground/80' : accentText[accent],
          )}
        >
          {value}
        </p>
        {sub && <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>}
        {demo && demoLabel && (
          <p className="mt-1.5 text-[11px] leading-tight text-muted-foreground/70">{demoLabel}</p>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Horizontal bar list — div + width%
// ---------------------------------------------------------------------------

export interface BarRow {
  label: string
  value: number
  /** optional formatted value override (e.g. money) */
  display?: string
  accent?: Accent
}

const barFill: Record<Accent, string> = {
  neutral: 'bg-slate-400',
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  primary: 'bg-primary',
}

export function BarList({
  rows,
  demo = false,
}: {
  rows: BarRow[]
  demo?: boolean
}) {
  const max = Math.max(1, ...rows.map((r) => r.value))
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => {
        const pct = Math.round((r.value / max) * 100)
        return (
          <div key={`${r.label}-${i}`} className="space-y-1">
            <div className="flex items-baseline justify-between text-xs">
              <span className="truncate text-foreground">{r.label}</span>
              <span className="ml-2 shrink-0 font-semibold tabular-nums text-muted-foreground">
                {r.display ?? r.value}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  demo ? 'bg-muted-foreground/40' : barFill[r.accent ?? 'primary'],
                )}
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Progress ring — SVG stroke-dasharray (0..1)
// ---------------------------------------------------------------------------

export function ProgressRing({
  value,
  label,
  caption,
  accent = 'primary',
  demo = false,
  size = 96,
}: {
  /** 0..1 */
  value: number
  /** center text */
  label: string
  caption?: string
  accent?: Accent
  demo?: boolean
  size?: number
}) {
  const stroke = 9
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const clamped = Math.max(0, Math.min(1, value))
  const dash = c * clamped

  const ringColor = demo
    ? 'stroke-muted-foreground/40'
    : accent === 'green'
      ? 'stroke-green-500'
      : accent === 'amber'
        ? 'stroke-amber-500'
        : accent === 'red'
          ? 'stroke-red-500'
          : 'stroke-primary'

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            className="stroke-muted"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            className={cn('transition-all', ringColor)}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className={cn(
              'text-lg font-bold tabular-nums',
              demo ? 'text-muted-foreground/80' : 'text-foreground',
            )}
          >
            {label}
          </span>
        </div>
      </div>
      {caption && (
        <span className="mt-1 text-center text-xs leading-tight text-muted-foreground">{caption}</span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Donut — conic-gradient with legend
// ---------------------------------------------------------------------------

export interface DonutSlice {
  label: string
  value: number
  color: string
}

export function Donut({
  slices,
  size = 132,
  centerTop,
  centerBottom,
}: {
  slices: DonutSlice[]
  size?: number
  centerTop?: React.ReactNode
  centerBottom?: React.ReactNode
}) {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1
  let acc = 0
  const stops = slices
    .map((s) => {
      const start = (acc / total) * 100
      acc += s.value
      const end = (acc / total) * 100
      return `${s.color} ${start}% ${end}%`
    })
    .join(', ')

  return (
    <div className="flex items-center gap-5">
      <div
        className="relative shrink-0 rounded-full"
        style={{ width: size, height: size, background: `conic-gradient(${stops})` }}
      >
        <div className="absolute inset-[18%] flex flex-col items-center justify-center rounded-full bg-card">
          {centerTop && <span className="text-lg font-bold leading-none tabular-nums">{centerTop}</span>}
          {centerBottom && (
            <span className="mt-0.5 text-[11px] leading-tight text-muted-foreground">{centerBottom}</span>
          )}
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-1.5">
        {slices.map((s) => (
          <li key={s.label} className="flex items-center gap-2 text-xs">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
            <span className="flex-1 truncate text-foreground">{s.label}</span>
            <span className="shrink-0 font-semibold tabular-nums text-muted-foreground">
              {s.value} · {Math.round((s.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Column chart (revenue by month) — vertical div bars
// ---------------------------------------------------------------------------

export function ColumnChart({
  data,
  format,
  demo = false,
}: {
  data: { label: string; value: number }[]
  format?: (n: number) => string
  demo?: boolean
}) {
  const max = Math.max(1, ...data.map((d) => d.value))
  return (
    <div className="flex h-32 items-end justify-between gap-2">
      {data.map((d, i) => {
        const h = Math.round((d.value / max) * 100)
        return (
          <div key={`${d.label}-${i}`} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
              {format ? format(d.value) : d.value}
            </span>
            <div className="flex h-full w-full items-end">
              <div
                className={cn(
                  'w-full rounded-t transition-all',
                  demo ? 'bg-muted-foreground/40' : 'bg-primary/80',
                )}
                style={{ height: `${Math.max(h, 3)}%` }}
              />
            </div>
            <span className="truncate text-[10px] text-muted-foreground">{d.label}</span>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sparkline — inline SVG path
// ---------------------------------------------------------------------------

export function Sparkline({
  points,
  demo = false,
  height = 48,
}: {
  points: number[]
  demo?: boolean
  height?: number
}) {
  const width = 220
  const max = Math.max(1, ...points)
  const min = Math.min(...points, 0)
  const range = max - min || 1
  const n = points.length
  const stepX = n > 1 ? width / (n - 1) : width

  const coords = points.map((p, i) => {
    const x = i * stepX
    const y = height - ((p - min) / range) * (height - 6) - 3
    return [x, y] as const
  })

  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const area = `${line} L${width},${height} L0,${height} Z`
  const color = demo ? 'hsl(var(--muted-foreground))' : 'hsl(var(--primary))'

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-12 w-full" preserveAspectRatio="none">
      <path d={area} fill={color} fillOpacity={demo ? 0.08 : 0.12} />
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {coords.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={2.5} fill={color} />
      ))}
    </svg>
  )
}
